import * as Api from '@ViewModels';
import { IRange } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { NotesDateFilterOptionValue, NotesFilterOptionValue, NotesSortOptionValue } from '../../../../models';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import { getDisplayName } from '../../../../models/NameUtils';
import { formatDateCriteria, formatDateRangeString } from '../../../../models/UiUtils';
import {
	NotesSearchSortByType,
	invalidateNotesByAccount,
	invalidateNotesByMe,
	invalidateNotesSearch,
	useDeleteNoteMutation,
	useInfiniteNotesSearch,
} from '../../../../queries';
import { INoteExportRequest, useExportNotesMutation } from '../../../../queries/Reports/useExportNotesMutation';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components-v2/Select';
import { NoteCard } from '../../cards/NoteCard';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { DateRangePickerSideModal } from '../../DateRangePickerSide';
import { ExportConfirmationModalV2 } from '../../ExportConfirmation';
import { FabCalloutPlaceholder } from '../../FabCalloutPlaceholder';
import { LoadingSpinner } from '../../LoadingSpinner';
import { LocalNotificationObserver } from '../../LocalNotificationObserver';
import { Portal } from '../../Portal';
import { NotesPlaceholderIcon } from '../../svgs/icons/NotesPlaceholderIcon';
import { SearchIcon } from '../../svgs/icons/SearchIcon';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { TextInput } from '../../TextInput';
import { UserSelectModal } from '../../UserSelectModal';
import { styleSheet } from './styles';

const NOTES_EXPORT_INCLUDE_EMAILS_KEY = 'notesExportIncludeEmails';

const readNotesExportIncludeEmails = (): boolean => {
	try {
		const raw = window.localStorage.getItem(NOTES_EXPORT_INCLUDE_EMAILS_KEY);
		return raw === 'true';
	} catch {
		return false;
	}
};

const writeNotesExportIncludeEmails = (includeEmails: boolean) => {
	try {
		window.localStorage.setItem(NOTES_EXPORT_INCLUDE_EMAILS_KEY, includeEmails ? 'true' : 'false');
	} catch {
		// ignore — localStorage may be unavailable in some browser modes
	}
};

export const NoteOptions: { label: string; value: NotesFilterOptionValue }[] = [
	{ label: 'Show all notes', value: 'byAccount' },
	{ label: 'Show my notes only', value: 'byMe' },
	{ label: 'Show notes owned by...', value: 'ownedBy' },
];

const DefaultDateRangeText = 'Filter by Date';
export const NoteDateOptions: { label: string; value: NotesDateFilterOptionValue }[] = [
	{ label: DefaultDateRangeText, value: 'date' },
];
export const NoteSortOptions: { label: string; value: NotesSortOptionValue }[] = [
	{ label: 'Last Modified date (newest first)', value: 'LastModifiedDate-dsc' },
	{ label: 'Last Modified date (oldest first)', value: 'LastModifiedDate-asc' },
	{ label: 'Creation date (newest first)', value: 'CreationDate-dsc' },
	{ label: 'Creation date (oldest first)', value: 'CreationDate-asc' },
];

interface IProps {
	scrollToBottomPortalId?: string;
	onEditNote: (noteId: string) => void;
}

function _NotesList({ onEditNote, scrollToBottomPortalId }: IProps) {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError, logEvent, logInput } = useEventLogging();
	const [searchQuery, setSearchQuery] = React.useState('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState(searchQuery);
	const [noteIdToDelete, setNoteIdToDelete] = React.useState<string | null>(null);
	const [selectedFilterOption, setSelectedFilterOption] = React.useState(NoteOptions[1]);
	const activeQueryType: 'search' | 'byMe' | 'byAccount' | 'ownedBy' = React.useMemo(() => {
		return debouncedSearchQuery ? 'search' : selectedFilterOption.value;
	}, [selectedFilterOption, debouncedSearchQuery]);
	const [dateRange, setDateRange] = React.useState<IRange<Date | null>>({
		start: null,
		end: null,
	});
	const [showDateSelector, setShowDateSelector] = React.useState(false);
	const [selectedDateRangeText, setSelectedDateRangeText] = React.useState(DefaultDateRangeText);
	const [selectedSortFilterOption, setSelectedSortFilterOption] = React.useState(NoteSortOptions[0]);
	const [showSelectEmployeeModal, setShowSelectEmployeeModal] = React.useState(false);
	const [assignedToUser, setAssignedToUser] = React.useState<Api.UserViewModel | undefined>();

	const filterRequest = React.useMemo(() => {
		const newFilterRequest: Api.IRichContentRequest = {
			criteria: [],
		};

		if (debouncedSearchQuery.length > 0) {
			newFilterRequest.criteria?.push({
				property: Api.RichContentProperty.Content,
				value: debouncedSearchQuery,
			});
		} else {
			newFilterRequest.criteria?.push({
				property: Api.RichContentProperty.All,
				value: 'All',
			});
		}
		if (dateRange?.start) {
			newFilterRequest.criteria?.push(
				formatDateCriteria<Api.RichContentProperty>(Api.RichContentProperty.CreationDate, dateRange)
			);
		}

		if (selectedFilterOption.value === 'ownedBy') {
			newFilterRequest.criteria?.push({
				property: Api.RichContentProperty.OwnedBy,
				value: assignedToUser?.id,
			});
		}

		return newFilterRequest;
	}, [debouncedSearchQuery, dateRange, selectedFilterOption.value, assignedToUser]);
	const sort = React.useMemo<Api.ISortDescriptor<NotesSearchSortByType>>(() => {
		let newSort: 'asc' | 'desc' = 'asc';
		let sortBy: NotesSearchSortByType = 'lastModifiedDate';
		switch (selectedSortFilterOption.value) {
			case 'LastModifiedDate-asc':
				newSort = 'asc';
				sortBy = 'lastModifiedDate';
				break;
			case 'LastModifiedDate-dsc':
				newSort = 'desc';
				sortBy = 'lastModifiedDate';
				break;
			case 'CreationDate-asc':
				newSort = 'asc';
				sortBy = 'creationDate';
				break;
			case 'CreationDate-dsc':
				newSort = 'desc';
				sortBy = 'creationDate';
				break;
			default:
				break;
		}
		return { sort: newSort, sortBy };
	}, [selectedSortFilterOption]);

	const onRangeChangedCallback = (range: IRange<Date | null>) => {
		const nextDateRange = {
			start: range.start,
			end: range.end,
		};

		setDateRange(nextDateRange);
	};

	const onSelectAnEmployeeClose = (user?: Api.UserViewModel, cancelled?: boolean) => {
		if (cancelled) {
			setSelectedFilterOption(NoteOptions[1]);
		} else {
			setSelectedFilterOption(NoteOptions[2]);
			setAssignedToUser(user);
		}
		setShowSelectEmployeeModal(false);
	};

	const notesSearchQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.value === 'byMe',
		enabled: activeQueryType === 'search',
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const notesByMeQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.value === 'byMe',
		enabled: activeQueryType === 'byMe',
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const notesByAccountQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.value === 'byMe',
		enabled: activeQueryType === 'byAccount',
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const notesOwnedByQuery = useInfiniteNotesSearch({
		currentUserOnly: selectedFilterOption.value === 'byMe',
		enabled: activeQueryType === 'ownedBy' && Boolean(assignedToUser),
		query: debouncedSearchQuery,
		filterRequest,
		sort: sort.sort,
		sortBy: sort.sortBy,
	});

	const handleInvalidateAllNotes = () => {
		invalidateNotesByAccount();
		invalidateNotesByMe();
		invalidateNotesSearch();
	};
	const deleteNoteMutation = useDeleteNoteMutation({
		onError: error => {
			logApiError('DeleteNote-Error', error);

			errorMessages.pushApiError(error);
		},
		onSuccess: handleInvalidateAllNotes,
	});
	const [exportTooLargeMessage, setExportTooLargeMessage] = React.useState<string | null>(null);
	const exportNotesMutation = useExportNotesMutation({
		onError: error => {
			if (error?.systemMessage?.includes('maximum export size')) {
				logEvent('ExportNotes-TooLarge', { systemMessage: error.systemMessage });
				setExportTooLargeMessage(error.systemMessage);
				return;
			}
			logApiError('ExportNotes-Error', error);
			errorMessages.pushApiError(error);
		},
	});
	const onExportTooLargeDialogClose = () => {
		setExportTooLargeMessage(null);
		exportNotesMutation.reset();
	};

	const isExportOwnerSelectionMissing = selectedFilterOption.value === 'ownedBy' && !assignedToUser;
	const isExportDisabled = isExportOwnerSelectionMissing || !exportNotesMutation.isIdle;
	const [showExportOptionsDialog, setShowExportOptionsDialog] = React.useState(false);
	const [exportIncludeEmails, setExportIncludeEmails] = React.useState(false);

	const onExportButtonClicked = () => {
		if (!exportNotesMutation.isIdle || isExportDisabled) {
			return;
		}
		setExportIncludeEmails(readNotesExportIncludeEmails());
		setShowExportOptionsDialog(true);
	};

	const onConfirmExportDialog = () => {
		writeNotesExportIncludeEmails(exportIncludeEmails);
		setShowExportOptionsDialog(false);
		const exportRequest: INoteExportRequest = { includeEmails: exportIncludeEmails };
		if (dateRange.start) {
			exportRequest.startDate = dateRange.start.toISOString();
		}
		if (dateRange.end) {
			exportRequest.endDate = dateRange.end.toISOString();
		}
		if (selectedFilterOption.value === 'byMe') {
			exportRequest.currentUserOnly = true;
		} else if (selectedFilterOption.value === 'ownedBy' && assignedToUser?.id) {
			exportRequest.ownedByUserIds = [assignedToUser.id];
		}
		logInput('ExportNotes', 'Click', {
			filterType: selectedFilterOption.value,
			hasDateRange: !!(exportRequest.startDate || exportRequest.endDate),
			includeEmails: exportRequest.includeEmails,
		});
		exportNotesMutation.mutate({ exportRequest });
	};

	const onExportModalRequestClose = () => {
		exportNotesMutation.reset();
	};
	React.useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 300);
		return () => {
			clearTimeout(handler);
		};
	}, [searchQuery]);
	const activeQuery = {
		byAccount: notesByAccountQuery,
		byMe: notesByMeQuery,
		search: notesSearchQuery,
		ownedBy: notesOwnedByQuery,
	}[activeQueryType];
	if (
		activeQueryType === 'byAccount' &&
		notesByAccountQuery.status === 'success' &&
		notesByAccountQuery.data.pages[0].values.length === 0
	) {
		return (
			<FabCalloutPlaceholder
				icon={<NotesPlaceholderIcon />}
				subtitle='Add your first note with the blue round button on the bottom right.'
				title='You don’t have any notes yet.'
			/>
		);
	}
	const onDeleteNoteConfirmationDialogRequestClose = (
		result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		if (result && !canceled && noteIdToDelete) {
			logEvent('DeleteNote', { id: noteIdToDelete });

			deleteNoteMutation.mutate({ id: noteIdToDelete });
		}
		setNoteIdToDelete(null);
	};

	return (
		<>
			<div className={css(styleSheet.searchHeader)}>
				<div>
					<div className={css(styleSheet.searchHeaderWrap)}>
						<TextInput
							className={css(styleSheet.searchField)}
							inputId='notes-search'
							leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} />}
							onChange={ev => setSearchQuery(ev.target.value)}
							placeholder='Search notes'
							type='text'
							value={searchQuery}
						/>
						{debouncedSearchQuery.length > 0 ? (
							<button onClick={() => setSearchQuery('')} className={css(styleSheet.clear)}>
								Clear
							</button>
						) : null}
					</div>
				</div>
				<div className={css(styleSheet.searchContainer)}>
					<Select
						value={selectedSortFilterOption.value}
						onValueChange={value => {
							const option = NoteSortOptions.find(o => o.value === value);
							if (option) {
								setSelectedSortFilterOption(option);
							}
						}}
					>
						<SelectTrigger styles={styleSheet.sortSelect}>
							<span>{selectedSortFilterOption.label}</span>
						</SelectTrigger>
						<SelectContent>
							{NoteSortOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select
						value={NoteDateOptions[0].value}
						onValueChange={() => {
							if (!dateRange?.start) {
								setDateRange({
									start: new Date(),
									end: new Date(),
								});
							}
							setShowDateSelector(true);
						}}
					>
						<SelectTrigger styles={styleSheet.dateSelect}>
							<span>{selectedDateRangeText}</span>
						</SelectTrigger>
						<SelectContent>
							{NoteDateOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<DateRangePickerSideModal
						isOpen={showDateSelector}
						onRequestClose={(clear = false) => {
							setShowDateSelector(false);
							if (clear) {
								setSelectedDateRangeText(DefaultDateRangeText);

								onRangeChangedCallback({ start: null, end: null });
							} else {
								setSelectedDateRangeText(formatDateRangeString(dateRange));
							}
						}}
						onRangeChange={onRangeChangedCallback}
						range={dateRange}
					/>
					<Select
						value={selectedFilterOption.value}
						onValueChange={value => {
							const option = NoteOptions.find(o => o.value === value);
							if (option) {
								setSelectedFilterOption(option);
								if (option.value === 'ownedBy') {
									setShowSelectEmployeeModal(true);
								}
							}
						}}
					>
						<SelectTrigger styles={styleSheet.filterSelect}>
							{selectedFilterOption.value === 'ownedBy' ? (
								<span>Owned by {getDisplayName(assignedToUser) || '...'}</span>
							) : (
								<span>{selectedFilterOption.label}</span>
							)}
						</SelectTrigger>
						<SelectContent>
							{NoteOptions.map(option => (
								<SelectItem key={option.value} value={option.value} styles={styleSheet.filterSelectOption}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<UserSelectModal
						bodyText='Filter notes by those owned by an employee.'
						// className={`${css(styleSheet.selectAnEmployeeModal)}`}
						modalProps={{
							isOpen: showSelectEmployeeModal,
							onRequestClose: onSelectAnEmployeeClose,
							shouldCloseOnOverlayClick: true,
						}}
						title='Select an Employee'
					/>
					<Button
						variant='primary'
						disabled={isExportDisabled}
						onClick={onExportButtonClicked}
						styles={[styleSheet.exportButton]}
						title={
							isExportOwnerSelectionMissing
								? 'Select an employee to export their notes'
								: !exportNotesMutation.isIdle
									? 'An export is already in progress'
									: 'Export the filtered notes to a CSV spreadsheet'
						}
					>
						Export
					</Button>
				</div>
			</div>

			<div className={css(styleSheet.notesList)}>
				{activeQuery.isPending ? <LoadingSpinner type='large' /> : null}
				{activeQuery.status === 'success'
					? activeQuery.data.pages.map(group =>
							group.values.map(note => {
								const noteVm = new Api.NoteViewModel(userSession, note);
								return (
									<NoteCard
										className={css(styleSheet.note)}
										key={note.id}
										note={noteVm}
										onEditNote={onEditNote}
										onDeleteNote={id => setNoteIdToDelete(id)}
									/>
								);
							})
						)
					: null}
				{activeQuery.hasNextPage && !activeQuery.isFetchingNextPage ? (
					<Portal destination={scrollToBottomPortalId}>
						<Waypoint key='notes-list' bottomOffset='-200px' onEnter={() => activeQuery.fetchNextPage()} />
					</Portal>
				) : null}
				{activeQuery.isFetchingNextPage ? <LoadingSpinner type='small' /> : null}
			</div>
			<LocalNotificationObserver topic={Topics.CREATE_NOTE} onNotificationReceived={handleInvalidateAllNotes} />
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!noteIdToDelete,
					onRequestClose: onDeleteNoteConfirmationDialogRequestClose,
				}}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Delete',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Are you sure you want to delete this note?'
			/>
			<Dialog open={showExportOptionsDialog} onOpenChange={setShowExportOptionsDialog}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Export notes</DialogTitle>
						<DialogDescription>
							We&apos;ll apply your selected date range and owner filter, then email you a CSV when it&apos;s ready.
						</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<label className={css(styleSheet.exportDialogCheckboxRow)}>
							<Checkbox
								checked={exportIncludeEmails}
								onCheckedChange={value => setExportIncludeEmails(value === true)}
							/>
							<span>Include emails and texts in the report</span>
						</label>
						<div className={css(styleSheet.exportDialogDescription)}>
							Uncheck to export only manual notes, meetings, and calls — not emails or texts sent through Levitate.
						</div>
					</DialogBody>
					<DialogFooter>
						<Button variant='secondary' onClick={() => setShowExportOptionsDialog(false)}>
							Cancel
						</Button>
						<Button variant='primary' onClick={onConfirmExportDialog}>
							Export
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
			{(exportNotesMutation.isPending || exportNotesMutation.isSuccess) && !exportTooLargeMessage ? (
				<ExportConfirmationModalV2
					mutationResult={exportNotesMutation}
					modalProps={{
						isOpen: exportNotesMutation.isPending || exportNotesMutation.isSuccess,
						onRequestClose: onExportModalRequestClose,
					}}
					subTitle={
						<>
							<div>The export includes: Date, Contacts, Companies, City, State, Note Type, Assigned, and Comments.</div>
							<div className={css(styleSheet.exportConfirmationNote)}>
								Your selected date range and owner filter are applied. Text search is not applied to exports.
							</div>
						</>
					}
				/>
			) : null}
			<Dialog
				open={!!exportTooLargeMessage}
				onOpenChange={open => {
					if (!open) {
						onExportTooLargeDialogClose();
					}
				}}
			>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Export too large</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<div>{exportTooLargeMessage}</div>
						<div className={css(styleSheet.exportTooLargeSuggestionsHeading)}>Try one of these:</div>
						<ul className={css(styleSheet.exportTooLargeSuggestions)}>
							<li>Narrow the date range</li>
							<li>Switch to &ldquo;Show my notes only&rdquo; or limit to a specific person</li>
							<li>Uncheck &ldquo;Include emails and texts in the report&rdquo;</li>
						</ul>
					</DialogBody>
					<DialogFooter>
						<Button variant='primary' onClick={onExportTooLargeDialogClose}>
							Close
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</>
	);
}

export const NotesList = observer(_NotesList);
